import clsx from 'clsx';
import { motion, useAnimation } from 'motion/react';
import { useEffect, useState, useCallback } from 'react';

import CityIcon from '@common/assets/icons/city.svg?react';
import HeartIcon from '@common/assets/icons/heart.svg?react';
import PencilIcon from '@common/assets/icons/pencil.svg?react';
import { usePreloadImages } from '@common/hooks';
import { Badge } from '@common/ui/Badge';
import { CachedImage } from '@common/ui/CachedImage';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { useSwipeAnimation } from '@entities/dating/hooks/useSwipeAnimation';
import * as dicts from '@entities/profile/dicts';
import {
  isIncomingSuperlikeCard,
  SuperlikeBadge,
  type SuperlikeAnimationPhase,
  superlikeCardMotions,
  superlikeCardMotionTarget,
  SuperlikeMessage,
  SuperlikeWash,
} from '@entities/superlike/ui';

import { type User } from '@services/UsersService';

import { ProfileCardSkeleton } from './components/ProfileCardSkeleton';
import styles from './ProfileCard.module.scss';

export type ProfileCardVariant = 'dating' | 'matchmaker';

/**
 * A superlike send in progress: the card that was sent ('sent') or the one
 * under it ('next'), and the animation's phase.
 */
export type ProfileCardSuperlikeSend = {
  phase: SuperlikeAnimationPhase;
  role: 'sent' | 'next';
};

export interface ProfileCardProps {
  profile: User | undefined;
  variant?: ProfileCardVariant;
  canSwipes?: boolean;
  backButton?: React.ReactNode;
  filtersButton?: React.ReactNode;
  overlay?: React.ReactNode;
  contentInsetRight?: number;
  superlikeSend?: ProfileCardSuperlikeSend;
  /** Set once: the card leaves this way, as after a swipe (a feed button). */
  flyOut?: 'left' | 'right';
  /**
   * True when a drag starts, false once it has settled (the card snapped
   * back, or flew out and onLike/onSkip ran). A fly-out counts as a drag.
   */
  onDragStateChange?: (isDragged: boolean) => void;
  onLike?: (profile: User) => void;
  onSkip?: (profile: User) => void;
  style?: React.CSSProperties;
}

const CONTENT_PADDING = 16;

const matchText = { 0: 'Вы ему нравитесь', 1: 'Вы ей нравитесь' };
const likeText = { 0: 'Поставим ему лайк?', 1: 'Поставим ей лайк?' };

const truncateText = (text: string, maxLength: number) =>
  text.length <= maxLength ? text : text.slice(0, maxLength) + '...';

export const ProfileCard = ({
  profile,
  variant = 'dating',
  canSwipes = true,
  backButton = false,
  filtersButton = false,
  overlay,
  contentInsetRight,
  superlikeSend,
  flyOut,
  onDragStateChange,
  onLike,
  onSkip,
  style,
}: ProfileCardProps) => {
  const controls = useAnimation();
  const auth = useAuthStore();
  const { preloadImages, imagesPreloaded } = usePreloadImages();

  const {
    dislikeGradientRef,
    flyOut: playFlyOut,
    handleDrag,
    handleDragEnd,
    likeGradientRef,
    opacity,
    rotate,
    x,
  } = useSwipeAnimation({ onLike, onSkip, profile });

  const [currentPage, setCurrentPage] = useState(0);

  const isDating = variant === 'dating';
  const isSwipeable = isDating && canSwipes && imagesPreloaded;
  const incomingSuperlike =
    isDating && isIncomingSuperlikeCard(profile) ? profile.superlike : null;
  const superlikeMotion = superlikeSend
    ? superlikeCardMotions(superlikeSend.phase)[superlikeSend.role]
    : undefined;
  const superlikeWashPhase =
    superlikeSend?.role === 'sent' && superlikeSend.phase !== 'idle'
      ? superlikeSend.phase
      : null;

  useEffect(() => {
    preloadImages(profile?.photos ?? []);
  }, [profile?.photos, preloadImages]);

  // Only a new direction starts it: the handlers change on every render.
  useEffect(() => {
    if (!flyOut) return;

    onDragStateChange?.(true);
    void playFlyOut(flyOut).then(() => onDragStateChange?.(false));
  }, [flyOut]);

  const onCardClick = useCallback(
    (side: 'left' | 'right') => {
      setCurrentPage((prevPage) => {
        const total = profile?.photos?.length || 1;

        if (side === 'left') return prevPage === 0 ? total - 1 : prevPage - 1;

        return (prevPage + 1) % total;
      });
    },
    [profile?.photos],
  );

  return (
    <motion.div
      data-test-id="profile-card-container"
      data-variant={variant}
      data-swipeable={isSwipeable}
      data-superlike-motion={superlikeMotion}
      className={styles.profileCard}
      style={{
        x,
        rotate,
        opacity,
        ...style,
      }}
      drag={isSwipeable ? 'x' : false}
      animate={
        superlikeMotion ? superlikeCardMotionTarget(superlikeMotion) : controls
      }
      onDragStart={() => onDragStateChange?.(true)}
      onDrag={handleDrag}
      onDragEnd={async (event, info) => {
        await handleDragEnd(event, info);
        onDragStateChange?.(false);
      }}
    >
      <div className={styles.pageNavigator}>
        <div
          className={styles.left}
          aria-label="Предыдущее фото"
          data-test-id="profile-card-page-left"
          onClick={() => onCardClick('left')}
        />
        <div
          className={styles.right}
          aria-label="Следующее фото"
          data-test-id="profile-card-page-right"
          onClick={() => onCardClick('right')}
        />
      </div>

      <div className={styles.gradient} data-type="default" />

      {superlikeWashPhase && <SuperlikeWash phase={superlikeWashPhase} />}

      {incomingSuperlike && (
        <div
          className={styles.superlikeFrame}
          aria-hidden="true"
          data-test-id="profile-card-superlike-frame"
        />
      )}

      {isDating && (
        <>
          <div
            ref={likeGradientRef}
            className={styles.gradient}
            data-type="like"
            data-test-id="profile-card-like-gradient"
          >
            <div className={styles.gradientIconContainer}>
              <CachedImage
                testId="cached-image"
                alt="badge"
                src="/icons/3d-icons/like.png"
                style={{ width: '216px' }}
              />
            </div>
          </div>
          <div
            ref={dislikeGradientRef}
            className={styles.gradient}
            data-type="dislike"
            data-test-id="profile-card-dislike-gradient"
          >
            <div className={styles.gradientIconContainer}>
              <CachedImage
                testId="cached-image"
                alt="badge"
                src="/icons/3d-icons/dislike.png"
                style={{ width: '216px' }}
              />
            </div>
          </div>
        </>
      )}

      <div className={styles.pageTabs}>
        {(profile?.photos ?? []).map((_, index) => (
          <div
            key={index}
            className={styles.tab}
            data-active={index === currentPage}
          />
        ))}
      </div>

      {backButton && <div className={styles.back}>{backButton}</div>}
      <div className={styles.filters}>{filtersButton}</div>

      {imagesPreloaded ? (
        (profile?.photos ?? []).map((photo, index) => (
          <div
            key={index}
            className={styles.imageContainer}
            style={{ opacity: index === currentPage ? 1 : 0 }}
          >
            <img src={photo} className={styles.image} alt="Фото пользователя" />
          </div>
        ))
      ) : (
        <ProfileCardSkeleton />
      )}

      <div
        className={clsx(styles.content, !imagesPreloaded && styles.loading)}
        data-test-id="profile-card-content"
        style={
          contentInsetRight
            ? { paddingRight: CONTENT_PADDING + contentInsetRight }
            : undefined
        }
      >
        {incomingSuperlike && (
          <>
            <SuperlikeBadge
              testId="profile-card-superlike-badge"
              text="Суперлайк вам"
            />
            {incomingSuperlike.message && (
              <SuperlikeMessage message={incomingSuperlike.message} />
            )}
          </>
        )}

        {isDating &&
          !incomingSuperlike &&
          profile?.liked_me &&
          auth?.user?.is_premium && (
            <Badge
              testId="profile-card-liked-me-badge"
              style={{ zoom: 0.7 }}
              color="action"
              prefix={<HeartIcon />}
              text={matchText[profile?.sex]}
            />
          )}

        <div className={styles.name} data-superlike={!!incomingSuperlike}>
          {profile?.username ? `${truncateText(profile.username, 13)}, ` : null}
          {profile?.age}
          <span className={styles.additional}>
            {profile?.is_premium && (
              <CachedImage
                testId="cached-image"
                alt="badge"
                src="/icons/3d-icons/fire.png"
                blur="2px"
                style={{ width: '24px' }}
              />
            )}
            {profile?.is_verified && (
              <CachedImage
                testId="cached-image"
                alt="badge"
                src="/icons/3d-icons/confirmed.png"
                blur="2px"
                style={{ width: '24px' }}
              />
            )}
          </span>
        </div>

        {currentPage === 0 && profile?.city && (
          <div className={styles.city} data-test-id="profile-card-city">
            <CityIcon /> {truncateText(profile.city, 15)}
          </div>
        )}

        {profile && (
          <div className={styles.about}>
            {currentPage === 0 && (
              <span data-test-id="profile-card-about">
                <PencilIcon />
                <span>{truncateText(profile.about, 200)}</span>
              </span>
            )}
            {currentPage === 1 && (
              <span data-test-id="profile-card-goal">
                {dicts.goalDisplayDict[profile.goal]}
              </span>
            )}
            {isDating && currentPage === 2 && (
              <span data-test-id="profile-card-like-question">
                {likeText[profile.sex]}
              </span>
            )}
          </div>
        )}

        {incomingSuperlike && (
          <span
            className={styles.superlikeHint}
            data-test-id="profile-card-superlike-hint"
          >
            Лайк — и у вас мэтч
          </span>
        )}
      </div>

      {overlay && (
        <div className={styles.overlay} data-test-id="profile-card-overlay">
          {overlay}
        </div>
      )}
    </motion.div>
  );
};
